import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { type Destinatario } from '@framework/api-client/api/inquiries-compile/schemas/destinatario.ts';
import { EnumDestinatarioStatoComunicazione } from '@framework/api-client/api/inquiries-compile/schemas/enumDestinatarioStatoComunicazione.ts';
import { EnumDestinatarioStatoProtocollazione } from '@framework/api-client/api/inquiries-compile/schemas/enumDestinatarioStatoProtocollazione.ts';
import { type DatiSessioneUtente } from '@framework/api-client/api/users/schemas/datiSessioneUtente.ts';

export class CommunicationLogic {
  static sender(communication: ComunicazioneDTO | null | undefined) {
    return (
      communication?.utenteMittente ??
      communication?.procedimentoMittente ??
      communication?.altroUfficioVariabileMittente
    );
  }

  static allRecipients(communication: ComunicazioneDTO | null | undefined) {
    return [
      ...(communication?.destinatariRichiedenti ?? []),
      ...(communication?.destinatariProcedimenti ?? []),
      ...(communication?.destinatariAltriUfficiVariabile ?? []),
      ...(communication?.destinatariCompetenzaSecondaria ?? []),
    ];
  }

  /**
   * Individua il destinatario "corrente" della comunicazione, quello a cui
   * si riferisce lo stato di lettura per chi sta guardando: nel backoffice
   * è il destinatario del procedimento corrente fra i gruppi di destinatari
   * di tipo ufficio, nel frontoffice il richiedente, dato che la richiesta
   * appartiene all'utente loggato.
   */
  static currentRecipient(
    communication: ComunicazioneDTO | null | undefined,
    input: {
      isBackoffice: boolean | null | undefined;
      procedureId: string | null | undefined;
    },
  ): Destinatario | undefined {
    const { isBackoffice, procedureId } = input;

    if (!isBackoffice) return communication?.destinatariRichiedenti?.[0];

    return [
      ...(communication?.destinatariProcedimenti ?? []),
      ...(communication?.destinatariAltriUfficiVariabile ?? []),
      ...(communication?.destinatariCompetenzaSecondaria ?? []),
    ].find(
      recipient =>
        recipient.idProcedimento && recipient.idProcedimento === procedureId,
    );
  }

  static isDraft(communication: ComunicazioneDTO | null | undefined) {
    return (
      communication != null &&
      !communication.flCaricataManualmente &&
      communication.dataInvio == null
    );
  }

  static hasRecipientErrors(recipient: Destinatario | null | undefined) {
    return (
      recipient?.statoComunicazione ==
        EnumDestinatarioStatoComunicazione.FALLIMENTO ||
      recipient?.statoProtocollazione ==
        EnumDestinatarioStatoProtocollazione.FALLIMENTO
    );
  }

  static canBeEdited(input: {
    isDraft: boolean;
    isEditable: boolean | null | undefined;
    isManual: boolean;
    isProcedureOpen: boolean | null | undefined;
    procedureId: string | null | undefined;
    parentProcedureId: string | null | undefined;
  }) {
    const {
      isDraft,
      isEditable,
      isManual,
      isProcedureOpen,
      procedureId,
      parentProcedureId,
    } = input;

    return (
      (isDraft && !!isEditable) ||
      (isManual &&
        isProcedureOpen &&
        // TODO. È un po' un hack, ma per ora non abbiamo un modo migliore per
        // riconoscere se stiamo vedendo la comunicazione come "Padre" o come "Endoprocedimento".
        // Assumiamo che sia "Padre" se siamo in backoffice e non c'è un altro
        // procedimento padre.
        procedureId != null &&
        parentProcedureId == null)
    );
  }

  static isTableViewEnabled() {
    return process.env.ENABLE_COMMUNICATION_TABLE === 'true';
  }

  static isVisible() {
    return process.env.HIDE_COMMUNICATIONS !== 'true';
  }

  /**
   * Ritorna true se la comunicazione è stata inviata dall'utente
   * indicato.
   */
  static isUserSender(
    user: DatiSessioneUtente | null | undefined,
    communication: ComunicazioneDTO | null | undefined,
  ): boolean {
    if (!communication) return false;
    if (!user) return false;

    // Se l'ID utente è uguale a quello del mittente
    if (communication.utenteMittente?.id === user.utenteLoggato?.idUtente) {
      return true;
    }

    // Se l'ufficio del mittente è uguale a quello dell'utente
    if (
      communication.procedimentoMittente?.id ===
      user.utenteLoggato?.utenteUfficis?.[0]?.idUfficio
    ) {
      return true;
    }

    return false;
  }
}
